import { SxProps, Theme } from '@mui/material/styles';
import { columnFlex, pagePadding, rowFlex } from '../../lib/styles/commonStyles';

export const rootGridSx = {
  ...pagePadding,
  marginBottom: '32px',
};

export const dropdownGridSx = {
  marginBottom: '36px',
  ...rowFlex,
  justifyContent: 'space-between',
  height: '32px',
};

export const searchBarSx: SxProps<Theme> = (theme: Theme) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#F5F5F5',
  borderRadius: 1,
  width: '100%',
  '& .MuiInputBase-root': {
    height: '32px',
  },
});

export const sectionsGridSx = {
  gap: '40px',
  ...columnFlex,
};

export const createButtonSx = (theme: Theme, isDarkMode: boolean) => ({
  backgroundColor: isDarkMode ? theme.palette.grey[600] : theme.palette.report.main,
  color: isDarkMode ? theme.palette.common.white : theme.palette.report.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.report.light,
  },
  '&:focus:hover': {
    backgroundColor: theme.palette.report.light,
  },
});

export const dialogActionsButtonsSx: SxProps<Theme> = (theme: Theme) => ({
  padding: '10px 22px 10px 22px',
  '&:hover': { backgroundColor: theme.palette.background.paper },
  '&:hover:focus': {
    color: theme.palette.report.main,
  },
  '&.MuiButton-contained': {
    backgroundColor: theme.palette.report.main,
    color: theme.palette.report.contrastText,
  },
  '&.MuiButton-text': {
    color: theme.palette.text.primary,
  },
});

export const dialogTitleSx = () => ({
  paddingBottom: '0px',
});

export const dialogActionsSx = () => ({
  padding: '14px 22px',
});

export const createDialogSx = {
  width: '100%',
  maxWidth: '780px',
};

export const linearProgressSx: SxProps<Theme> = (theme: Theme) => ({
  '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.report.main },
  backgroundColor: theme.palette.divider,
  height: '5px',
});

export const closeButtonSx: SxProps<Theme> = (theme: Theme) => ({
  position: 'absolute',
  right: 8,
  top: 8,
  color: theme.palette.grey[500],
});

export const scheduleIconButtonSx: SxProps<Theme> = (theme: Theme) => ({
  borderRadius: '8px',
  bottom: '2px',
});

export const tabsSx = (theme: Theme, isDarkMode: boolean) => ({
  paddingTop: '24px',
  '& .MuiTab-root.Mui-selected': {
    color: isDarkMode ? theme.palette.grey[300] : theme.palette.report.main,
    fontWeight: 600,
  },
  '& .MuiTabs-indicator': { backgroundColor: isDarkMode ? theme.palette.grey[300] : theme.palette.report.main },
});

export const customReportTabGridSx = (theme: Theme, isDarkMode: boolean, padded: boolean) => ({
  border: `1px solid ${isDarkMode ? theme.palette.grey[800] : theme.palette.grey[300]}`,
  borderRadius: '8px',
  padding: padded ? '16px' : '0px',
  overflowY: 'auto',
  maxHeight: '440px',
  width: '100%',
  marginBottom: '8px',
  [theme.breakpoints.up('md')]: {
    maxWidth: '48%',
    marginBottom: '0px',
  },
  '& .MuiPaper-outlined': {
    border: 'none',
    boxShadow: 'none',
    padding: '0px',
    overflow: 'auto',
  },
});

export const accordionSx = {
  boxShadow: 'none',
  border: 'none',
  '&:before': { display: 'none' },
  '&.MuiAccordion-gutters': {
    margin: 0,
  },
};

export const accordionSummarySx = {
  '&.Mui-expanded': {
    height: '20px',
    minHeight: '35px',
    paddingLeft: '0px',
  },
  '&.MuiAccordionSummary-root': {
    paddingLeft: '0px',
    paddingRight: '0px',
  },
};

export const accordionDetailsSx = {
  padding: '0px 0px 16px 0px',
};

export const tabPanelSx = {
  padding: '24px 0px 0px 0px',
  overflowY: 'scroll',
};

export const dividerSx = () => ({
  margin: '2px',
});

export const selectAllSwitchSx = (theme: Theme, isDarkMode: boolean) => ({
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: isDarkMode ? theme.palette.grey[500] : theme.palette.report.main,
  },
  '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
    cursor: 'default',
  },
});

export const datasetCheckboxAndSwitchSx = (theme: Theme, isDarkMode: boolean) => ({
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: isDarkMode ? theme.palette.grey[500] : theme.palette.report.main,
  },
  '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
    cursor: 'default',
  },
  '&.Mui-checked': {
    color: isDarkMode ? theme.palette.grey[300] : theme.palette.report.main,
  },
  '&:hover': { backgroundColor: 'transparent' },
});

export const noSearchResultsSx = {
  padding: '20px',
};
export const boldLabelSx = {
  fontWeight: 500,
};

export const searchBarInlineSx = {
  marginLeft: '0px',
  marginRight: '0px',
};

export const selectAllFormControlLabelSx = (disabled: boolean) => ({
  justifyContent: 'flex-start',
  m: 0,
  width: 'fit-content',
  '.MuiFormControlLabel-label': { pl: 0, pr: 1 },
  cursor: disabled ? 'default' : 'pointer',
});

export const chipColorSx: SxProps<Theme> = (theme: Theme) => ({
  '&.MuiChip-filledPrimary': {
    backgroundColor: theme.palette.report.subtle,
  },
  '&.MuiChip-filledPrimary:focus, &.MuiChip-filledPrimary:hover': {
    backgroundColor: theme.palette.report.subtleHover,
  },
  '& .MuiChip-label': {
    color: theme.palette.report.darkAccent,
  },
  '& .MuiChip-icon, & .MuiChip-deleteIcon': {
    color: theme.palette.report.darkAccent,
    '&:hover': {
      color: theme.palette.report.darkAccent,
    },
  },
});

export const loadingSx = () => ({ margin: 'auto', display: 'flex' });

export const customMetricHelperTextSx: SxProps<Theme> = (theme: Theme) => ({
  color: theme.palette.text.secondary,
  display: 'block',
  fontSize: '11px',
  lineHeight: 1.2,
  marginTop: '2px',
});

export const selectUserFormControlSx: SxProps<Theme> = (theme: Theme) => ({
  padding: theme.spacing(1, 0),
});

// ScheduleReportDialog styles
export const scheduleReportDialogSx = {
  borderRadius: 2,
  padding: 2,
};

export const scheduleReportDialogTitleSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

export const scheduleReportChooseTextSx = {
  mb: 2,
};

export const scheduleReportLabelTextSx = {
  mb: 1,
};

export const scheduleReportDialogActionsSx = {
  justifyContent: 'flex-end',
  padding: 2,
};

export const scheduleReportCancelButtonSx = {
  mr: 2,
};

export const scheduleReportNextButtonSx = (theme: Theme) => ({
  bgcolor: theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    bgcolor: theme.palette.primary.main,
  },
});
export const contentTextSx = {
  paddingTop: '20px',
  paddingBottom: '10px',
  fontSize: '12px',
};

export const headerActionsContainerSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};
